import { useState } from "react";
import { Download } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { TRACK_SOURCE_GPX, type TrackSourceGpxResult } from "../lib/queries";
import { download, geometryToGeoJson, slugify, trackToGpx } from "../lib/export";
import { useT } from "../lib/i18n";
import type { Geometry } from "../lib/geo";
import { Button } from "@/components/ui/button";

/**
 * GPX / GeoJSON download buttons for a track. GPX prefers the verbatim
 * `sourceGpx` upload when the row still carries one (fetched lazily on click —
 * it can be large), and falls back to serializing the cleaned geometry.
 */
export function TrackExportButtons({
  trackId,
  geom,
  pointTimes,
  name,
}: {
  trackId: string;
  geom: Geometry;
  pointTimes?: string[] | null;
  name: string;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportGpx() {
    setBusy(true);
    setError(null);
    try {
      const data = await gqlRequest<TrackSourceGpxResult>(TRACK_SOURCE_GPX, { id: trackId });
      const gpx = data.track?.sourceGpx ?? trackToGpx(geom, name, pointTimes);
      download(`${slugify(name)}.gpx`, "application/gpx+xml", gpx);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="sm" onClick={exportGpx} disabled={busy} className="text-xs">
        <Download aria-hidden className="size-3.5" />
        {busy ? t("export.busy") : "GPX"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => download(`${slugify(name)}.geojson`, "application/geo+json", geometryToGeoJson(geom))}
      >
        <Download aria-hidden className="size-3.5" />
        GeoJSON
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
